import _ from 'underscore'
import dateToString from 'lib/date_to_string'
import ApplicationController from 'controllers/application_controller'
import BookableSelectController from 'controllers/realtime_widget/bookable_select_controller'
import BookableController from 'controllers/realtime_widget/bookable_controller'

export default class extends ApplicationController {

  // SETUP:

  static targets = [
    'stepOne',
    'stepTwo',
    'nextStepButton',
    'squeezeInfo',
    'squeezeInput',
    'squeezeFrom',
    'squeezeTill',
    'availabilityInfo',
    'guestsStatus',
    'dateStatus',
    'timeStatus',
    'debugStatus',
    'showDepositDescription',
    'depositDescription',
    'depositNumberOfGuests',
    'depositTotalCost',
    'depositCostPerPerson',
    'depositWarning',
    'reservationPayment',
    'guestNameInput',
    'guestTelephoneInput',
    'guestEmailInput',
    'commentsInput',
  ]

  static values = {
    // maximumGroupSize is currently not used
    maximumGroupSize: Number,
    stepOneTitle:     { type: String, default: 'Reserve a table'       },
    stepTwoTitle:     { type: String, default: 'Enter contact details' },
    stepThreeTitle:   { type: String, default: 'Reservation details'   },
    currentStep:      { type: Number, default: 1 },
    timeslots:        { type: Array, default: [] }
  }

  static classes = [
    'disabled',
    'isActive',
    'reservationsWidgetStepped',
    'current',
    'isVisible',
    'isHidden'
  ]

  static outlets = [
    'realtime-widget--bookable-select',
    'realtime-widget--date-select',
    'realtime-widget--time-select',
    'realtime-widget--guests-select'
  ]

  /**
   * @returns {BookableSelectController}
   */
  get bookableSelect()  {
    return this.realtimeWidgetBookableSelectOutlet
  }

  /**
   * @returns {BookableController}
   */
  get booking() {
    return this.bookableSelect && this.bookableSelect.booking
  }

  /**
   * @returns {DateSelectController}
   */
  get dateSelect()  {
    return this.realtimeWidgetDateSelectOutlet
  }

  /**
   * @returns {TimeSelectController}
   */
  get timeSelect()  {
    return this.realtimeWidgetTimeSelectOutlet
  }

  /**
   * @returns {GuestsSelectController}
   */
  get guestsSelect()  {
    return this.realtimeWidgetGuestsSelectOutlet
  }

  // INITIALIZATION:

  connect() {
    _.defer(() => {
      this.initializeWidget()
    })
  }

  disconnect() {
    if (this.embedded) {
      this.embedded.reportWidgetSize()
    }
  }

  initializeWidget() {
    this.showCurrentStep()

    if (this.embedded) {
      this.embedded.onExpand(this.resetWidgetState.bind(this))
      this.reportEvent('ready')
    }
  }

  // EMBEDDED WIDGET API:

  reportEvent(event, object = null) {
    if (this.embedded) {
      this.embedded.reportWidgetEvent(event, object)
    }
  }

  set(key, value) {
    switch (key.toLowerCase()) {
      case 'offer':
        this.bookableSelect.selectBookable(parseInt(value))
        break

      case 'date':
        this.dateSelect.value = value
        break

      case 'time':
        this.timeSelect.value = value
        break

      case 'partysize':
      case 'guests':
        this.guestsSelect.value = value
        break

      case 'guestname':
      case 'name':
        this.guestNameInputTarget.value = value
        break

      case 'guesttelephone':
      case 'telephone':
        this.guestTelephoneInputTarget.value = value
        break

      case 'guestemail':
      case 'email':
        this.guestEmailInputTarget.value = value
        break

      case 'comments':
        this.commentsInputTarget.value = value
        break
    }
  }

  // REFRESH:

  refresh() {
    if (!this.booking) {
      return
    }

    this.bookableSelect.bookables.forEach((bookable) => bookable.refresh())

    if (this.currentStepValue < 3) {
      this.toggleNextStep()

      this.dateStatusTarget.innerText   = this.booking.dateSelect.humanValue
      this.timeStatusTarget.innerText   = this.booking.timeSelect.value
      this.guestsStatusTarget.innerText = this.booking.guestsSelect.label

      this.refreshSqueeze()
      this.refreshRealtime()
      this.refreshDebug()

      if (this.embedded) {
        this.embedded.reportWidgetSize()
      }
    }
  }

  refreshSqueeze() {
    if (this.hasSqueezeInputTarget) {
      this.squeezeInputTarget.value = 'off'
      this.squeezeInfoTarget.classList.remove(this.isActiveClass)

      if (this.booking.isSqueezed) {
        this.squeezeInputTarget.value = 'on'
        this.squeezeInfoTarget.classList.add(this.isActiveClass)

        this.morph(this.squeezeFromTarget, this.booking.squeezedStartTime)
        this.morph(this.squeezeTillTarget, this.booking.squeezedEndTime)
      }
    }
  }

  refreshRealtime() {
    if (this.booking.canConfirmRealtime) {
      if (this.hasAvailabilityInfoTarget) {
        this.availabilityInfoTarget.classList.add(this.isActiveClass)
      }
    } else {
      if (this.hasAvailabilityInfoTarget) {
        this.availabilityInfoTarget.classList.remove(this.isActiveClass)
      }

      if (this.booking.rejectValue || this.booking.isDisabledDate()) {
        this.nextStepButtonTarget.classList.add(this.disabledClass)
      }
    }
  }

  refreshDebug() {
    if (this.hasDebugStatusTarget) {
      let booking       = this.booking
      let guests        = booking.guests
      let date          = booking.date && booking.date.toDateString()
      let time          = booking.time
      let availability  = booking.availability
      let timeslots     = JSON.stringify(availability && availability.timeslots)
      let normalSeats   = JSON.stringify(availability && availability.seatsAvailable && availability.seatsAvailable.maximum)
      let squeezedSeats = JSON.stringify(availability && availability.squeezedSeatsAvailable && availability.squeezedSeatsAvailable.maximum)

      this.morph(this.debugStatusTarget, [
        `random: ${Math.round(Math.random() * 1000)}`,
        `step: ${this.currentStepValue}`,
        `time: ${time}`,
        `guests: ${guests}`,
        `date: ${date}`,
        `is unavailable date: ${booking.isUnavailableDate()}`,
        `is disabled date: ${booking.isDisabledDate()}`,
        `realtime: ${booking.realtimeValue ? 'true' : 'false'}, reject: ${booking.rejectValue ? 'true' : 'false'}`,
        `maximum: ${booking.maximumSeatsAvailableForTimeslot()}, squeezed: ${booking.maximumSeatsAvailableForTimeslot(time, true)}`,
        `minimum: ${booking.minimumSeatsAvailableForTimeslot()}, squeezed: ${booking.minimumSeatsAvailableForTimeslot(time, true)}`,
        `can confirm: ${booking.canConfirmRealtime}, is squeezed: ${booking.isSqueezed}`,
        `bookable: ${this.bookableSelect ? this.bookableSelect.label || 'no' : 'disabled'}`,
        `timeslots: ${timeslots}`,
        `availabilities: ${booking.availabilitiesValue && booking.availabilitiesValue.length}`,
        `normal seats:\n${normalSeats}\n`,
        `squeezed seats:\n${squeezedSeats}\n`
      ].join("\n"))
    }
  }

  // HELPERS:

  isDisabledDate (date) {
    return this.bookableSelect && this.bookableSelect.isDisabledDate(date)
  }

  // MULTI STEPS:

  resetWidgetState () {
    if (this.booking.dateSelect) {
      this.booking.dateSelect.closeDatePicker()
    }

    if (this.hasStepOneTarget) {
      this.firstStep()
    }
  }

  toggleNextStep() {
    this.nextStepButtonTarget.classList.remove(this.disabledClass)

    const disable = (
      !this.booking.dateSelect.value   || !this.booking.timeSelect.value  ||
      !this.booking.guestsSelect.value || this.bookableSelect.needsSelection
    )

    if (disable) {
      this.nextStepButtonTarget.classList.add(this.disabledClass)
    }
  }

  firstStep() {
    this.currentStepValue = 1
    this.showCurrentStep()
  }

  previousStep(event) {
    if (event) { event.preventDefault() }
    this.firstStep()
  }

  nextStep (event) {
    if (event) { event.preventDefault() }

    if (!this.nextStepButtonTarget.classList.contains(this.disabledClass)) {
      this.currentStepValue = 2
      this.showCurrentStep()
    }
  }

  showCurrentStep() {
    this.element.classList.add(this.reservationsWidgetSteppedClass)

    let title = null

    if (this.currentStepValue === 2) {
      this.stepOneTarget.classList.remove(this.currentClass)
      this.stepTwoTarget.classList.add(this.currentClass)

      title = this.stepTwoTitleValue
    } else if (this.currentStepValue === 3) {
      title = this.stepThreeTitleValue
    } else {
      this.stepOneTarget.classList.add(this.currentClass)
      this.stepTwoTarget.classList.remove(this.currentClass)

      title = this.stepOneTitleValue
    }

    if (this.embedded) {
      this.embedded.reportWidgetSize()

      if (title) {
        this.embedded.title = title
      }
    }

    this.reportEvent('navigated', { section: `step_${this.currentStepValue}` })
  }

  showDepositDescription (event) {
    if (event) { event.preventDefault() }

    this.showDepositDescriptionTarget.classList.add(this.isHiddenClass)
    this.depositDescriptionTarget.classList.remove(this.isHiddenClass)


    if (this.embedded) {
      this.embedded.reportWidgetSize()
    }
  }
}
